import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WeatherCardComponent } from '../../../../shared/components/cards/weather-card.component';
import { HistoryCardComponent } from '../../../../shared/components/cards/history-card.component';
import { WeatherConditionsApiService } from '../../services/api/weather-conditions.api.service';
import { WeatherStationLastDaysModel } from '../../models/weather-station-last-days';
import { WeatherStationRealTimeModel } from '../../models/weather-station-real-time';
import { WeatherStationRealTimeByCityModel } from '../../models/weather-station-real-time-by-city';
import { WeatherStationDailyAverageModel } from '../../models/weather-station-daily-average';
import { CityWithActiveStationModel } from '../../models/cities-stations-active';

@Component({
  selector: 'app-weather-conditions',
  standalone: true,
  imports: [WeatherCardComponent, HistoryCardComponent, DatePipe, FormsModule],
  templateUrl: './weather-conditions.component.html',
  styleUrls: ['./weather-conditions.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class WeatherConditionsComponent implements OnInit {
  private readonly DEFAULT_CITY = 'Guaxupé - MG';
  private readonly DEFAULT_LATITUDE = -21.29703;
  private readonly DEFAULT_LONGITUDE = -46.72544;

  // Mensagens de erro
  private readonly ERR_NO_DATA_COMBINED =
    'Nenhum dado disponível para esta estação no momento: sem dados em tempo real e sem histórico dos últimos 5 dias.';
  private readonly ERR_SERVER_UNAVAILABLE =
    'Não foi possível retornar os dados das estações meteorológicas devido a um problema no servidor. Por favor, acesse o Sismet Web novamente mais tarde.';
  private readonly ERR_NO_HISTORY =
    'Nenhum dado histórico disponível para esta estação nos últimos 5 dias';
  private readonly ERR_LOAD_CLIMATE =
    'Não foi possível carregar os dados climáticos. Tente novamente mais tarde.';
  private readonly ERR_LOAD_HISTORY =
    'Não foi possível carregar os dados históricos. Tente novamente mais tarde.';
  private readonly ERR_LOAD_HISTORY_BY_CITY =
    'Não foi possível carregar o histórico dos últimos 5 dias para esta estação. Tente novamente mais tarde.';
  private readonly ERR_NO_REALTIME_STATION =
    'Nenhum dado em tempo real disponível para esta estação no momento.';
  private readonly ERR_NO_REALTIME_CITY =
    'Nenhum dado em tempo real disponível para esta cidade no momento.';
  private readonly ERR_NO_NEAREST_STATION =
    'Nenhuma estação meteorológica encontrada próxima à sua localização';
  private readonly ERR_CITY_NOT_SELECTED =
    'Por favor, selecione uma cidade válida';
  private readonly ERR_CITY_NOT_FOUND = 'Cidade não encontrada';

  // Cidade padrão utilizada ao carregar o componente
  selectedCity: string = this.DEFAULT_CITY;
  isDetectingLocation: boolean = false;
  isManualSelection: boolean = false; // Flag para indicar se foi seleção manual

  // Coordenadas da cidade de Guaxupé - MG
  selectedLatitude: number = this.DEFAULT_LATITUDE;
  selectedLongitude: number = this.DEFAULT_LONGITUDE;

  // ID da estação meteorológica retornada pela API
  weatherStationId: number | null = null;

  // Lista de cidades com estações ativas
  citiesWithActiveStations: CityWithActiveStationModel[] = [];
  selectedCityId: number | null = null;
  citySelectionError: string = '';

  // Dados dos últimos 5 dias
  lastDaysData: WeatherStationLastDaysModel[] = [];
  lastDaysError: string = '';
  private lastDaysSettled: boolean = false;
  private lastDaysNoData: boolean = false; // true apenas quando API confirma ausência de dados (404 ou array vazio)

  // Dados em tempo real
  realTimeData: WeatherStationRealTimeModel | null = null;
  realTimeError: string = '';

  // Dados em tempo real por cidade
  realTimeByCityData: WeatherStationRealTimeByCityModel | null = null;

  // Média diária de temperatura
  dailyAverageData: WeatherStationDailyAverageModel | null = null;

  constructor(private readonly weatherService: WeatherConditionsApiService) {}

  ngOnInit(): void {
    // Buscar lista de cidades com estações ativas
    this.fetchCitiesWithActiveStations();

    // Verificar permissão de geolocalização antes de tentar detectar
    this.checkGeolocationPermission();
  }

  private checkGeolocationPermission(): void {
    // Verificar se está em HTTP (Chrome bloqueia geolocalização em HTTP)
    const isSecureContext = globalThis.isSecureContext;
    const protocol = globalThis.location.protocol;

    if (!isSecureContext && protocol === 'http:') {
      this.detectUserLocation();
      return;
    }

    if (!navigator.permissions) {
      this.detectUserLocation();
      return;
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') {
          this.detectUserLocation();
        } else if (result.state === 'prompt') {
          this.detectUserLocation();
        } else if (result.state === 'denied') {
          this.fetchNearestStation(
            this.DEFAULT_LATITUDE,
            this.DEFAULT_LONGITUDE,
            false,
          );
          return;
        }

        // Monitorar mudanças na permissão
        result.onchange = () => {
          if (result.state === 'granted') {
            this.detectUserLocation();
          }
        };
      })
      .catch((error) => {
        // Tenta solicitar diretamente
        this.detectUserLocation();
      });
  }

  detectUserLocation(): void {
    if (!navigator.geolocation) {
      this.fetchNearestStation(
        this.DEFAULT_LATITUDE,
        this.DEFAULT_LONGITUDE,
        false,
      );
      return;
    }

    this.isDetectingLocation = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Arredondar para 7 casas decimais (formato aceito pela API)
        const userLat = Number(position.coords.latitude.toFixed(7));
        const userLng = Number(position.coords.longitude.toFixed(7));

        this.isDetectingLocation = false;

        // Buscar estação mais próxima na API com as coordenadas do usuário
        this.fetchNearestStation(userLat, userLng);
      },
      (error) => {
        this.isDetectingLocation = false;

        // Busca estação com coordenadas padrão
        this.fetchNearestStation(
          this.DEFAULT_LATITUDE,
          this.DEFAULT_LONGITUDE,
          false,
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }

  // Buscar estação meteorológica mais próxima na API
  private fetchNearestStation(
    latitude: number,
    longitude: number,
    allowDefaultFallback = true,
  ): void {
    this.weatherService.getNearestStation(latitude, longitude).subscribe({
      next: (station) => {
        this.weatherStationId = station.id;
        this.selectedCity =
          station.displayLocation?.trim() ||
          (allowDefaultFallback
            ? 'Estação mais próxima de você'
            : this.DEFAULT_CITY);
        this.selectedLatitude = latitude;
        this.selectedLongitude = longitude;

        // Limpar erros anteriores
        this.realTimeError = '';
        this.isManualSelection = false; // Geolocalização automática

        // Buscar dados em tempo real
        this.fetchRealTimeData(this.weatherStationId);

        // Buscar média diária de temperatura
        this.fetchDailyAverage(this.weatherStationId);

        // Buscar dados dos últimos 5 dias
        this.fetchLastDaysData(this.weatherStationId);
      },
      error: (error) => {
        if (error.status === 404 && allowDefaultFallback) {
          this.selectedCity = this.DEFAULT_CITY;
          this.fetchNearestStation(
            this.DEFAULT_LATITUDE,
            this.DEFAULT_LONGITUDE,
            false,
          );
          return;
        }

        if (error.status === 404) {
          this.realTimeError = this.ERR_NO_NEAREST_STATION;
        } else if (error.status === 0) {
          this.realTimeError = this.ERR_SERVER_UNAVAILABLE;
        } else {
          this.realTimeError = this.ERR_LOAD_CLIMATE;
        }
      },
    });
  }

  // Buscar dados dos últimos 5 dias
  private fetchLastDaysData(stationId: number): void {
    this.lastDaysSettled = false;
    this.lastDaysNoData = false;
    this.lastDaysError = '';
    this.weatherService.getLastDays(stationId, 5).subscribe({
      next: (data) => {
        this.lastDaysSettled = true;
        this.lastDaysNoData = data.length === 0;
        // Manter ordem da API: data mais recente primeiro, mais antiga por último
        this.lastDaysData = data;
        // Se o histórico voltou vazio e o tempo real já falhou, exibir mensagem combinada
        if (
          data.length === 0 &&
          this.realTimeData === null &&
          this.realTimeError
        ) {
          this.realTimeError = this.ERR_NO_DATA_COMBINED;
        }
      },
      error: (error) => {
        this.lastDaysSettled = true;

        this.lastDaysData = [];

        if (error.status === 404) {
          this.lastDaysNoData = true;
          // Se o tempo real também já falhou, exibir mensagem combinada em realTimeError
          if (this.realTimeData === null && this.realTimeError !== '') {
            this.realTimeError = this.ERR_NO_DATA_COMBINED;
          } else {
            this.lastDaysError = this.ERR_NO_HISTORY;
          }
        } else if (error.status === 0) {
          this.lastDaysError = this.ERR_SERVER_UNAVAILABLE;
        } else {
          this.lastDaysError = this.ERR_LOAD_HISTORY;
        }
      },
    });
  }

  // Buscar dados em tempo real
  private fetchRealTimeData(stationId: number): void {
    this.realTimeError = '';

    this.weatherService.getRealTimeData(stationId).subscribe({
      next: (data) => {
        this.realTimeData = data;
        this.realTimeError = ''; // Garantir que o erro está limpo
      },
      error: (error) => {
        this.realTimeData = null;

        // Definir mensagem de erro específica
        if (error.status === 404) {
          // Se o histórico já finalizou sem dados (404 ou array vazio), exibir mensagem combinada
          if (this.lastDaysSettled && this.lastDaysNoData) {
            this.realTimeError = this.ERR_NO_DATA_COMBINED;
            this.lastDaysError = '';
          } else {
            this.realTimeError = this.ERR_NO_REALTIME_STATION;
          }
        } else if (error.status === 0) {
          this.realTimeError = this.ERR_SERVER_UNAVAILABLE;
        } else {
          this.realTimeError = this.ERR_LOAD_CLIMATE;
        }
      },
    });
  }

  // Buscar média diária de temperatura
  private fetchDailyAverage(stationId: number): void {
    this.weatherService.getDailyAverage(stationId).subscribe({
      next: (data) => {
        this.dailyAverageData = data;
      },
      error: (error) => {
        // Limpar dados anteriores mas não mostrar erro crítico
        // (a média é opcional, não deve quebrar a página)
        this.dailyAverageData = null;
      },
    });
  }

  // Buscar cidades com estações ativas
  private fetchCitiesWithActiveStations(): void {
    this.weatherService.getCitiesWithActiveStations().subscribe({
      next: (cities) => {
        this.citiesWithActiveStations = cities;
      },
      error: (error) => {
        // Se não conseguir carregar as cidades, o select ficará vazio
        // O usuário ainda poderá ver os dados da localização detectada automaticamente
      },
    });
  }

  // Manipular submissão do formulário de seleção de cidade
  onCitySubmit(event: Event): void {
    event.preventDefault();

    // Limpar mensagem de erro anterior
    this.citySelectionError = '';

    // Verificar se uma cidade válida foi selecionada
    if (!this.selectedCityId) {
      this.citySelectionError = this.ERR_CITY_NOT_SELECTED;
      return;
    }

    const selectedCity = this.citiesWithActiveStations.find(
      (city) => city.id === this.selectedCityId,
    );

    if (selectedCity) {
      this.selectedCity = `${selectedCity.name} - ${selectedCity.state.code}`;

      // Marcar como seleção manual
      this.isManualSelection = true;

      // Buscar dados dos últimos 5 dias da cidade selecionada
      this.fetchLastDaysByCityData(selectedCity.id);
    } else {
      this.citySelectionError = this.ERR_CITY_NOT_FOUND;
    }
  }

  // Manipular mudança de seleção de cidade
  onCityChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    // Se o valor for vazio ou "null", define como null
    if (!value || value === 'null' || value === '') {
      this.selectedCityId = null;
    } else {
      this.selectedCityId = Number(value);
    }

    this.citySelectionError = '';
  }

  // Buscar dados dos últimos 5 dias por cidade
  private fetchLastDaysByCityData(cityId: number): void {
    // Limpar erros anteriores
    this.lastDaysError = '';
    this.realTimeError = '';
    this.lastDaysSettled = false;
    this.lastDaysNoData = false;

    // Buscar dados em tempo real da cidade
    this.fetchRealTimeDataByCity(cityId);

    // Buscar dados dos últimos 5 dias
    this.weatherService.getLastDaysByCity(cityId, 5).subscribe({
      next: (data) => {
        // Manter ordem da API: data mais recente primeiro, mais antiga por último
        this.lastDaysData = data as any;
        this.lastDaysError = ''; // Limpar erro quando há sucesso
      },
      error: (error) => {
        // Limpar dados anteriores quando não houver dados para a cidade
        this.lastDaysData = [];

        // Mensagem mais específica para a seção de últimos 5 dias
        if (error.status === 400 || error.status === 404) {
          this.lastDaysError = this.ERR_NO_HISTORY;
        } else if (error.status === 0) {
          this.lastDaysError = this.ERR_SERVER_UNAVAILABLE;
        } else {
          this.lastDaysError = this.ERR_LOAD_HISTORY_BY_CITY;
        }
      },
    });
  }

  // Buscar dados em tempo real por cidade
  private fetchRealTimeDataByCity(cityId: number): void {
    this.weatherService.getRealTimeDataByCity(cityId).subscribe({
      next: (data) => {
        // Verificar se data é null (API retornou sucesso mas sem dados)
        if (!data) {
          // Limpar dados anteriores
          this.realTimeByCityData = null;
          this.realTimeData = null;
          this.dailyAverageData = null;

          // Definir mensagem de erro específica
          this.realTimeError = this.ERR_NO_REALTIME_CITY;
          return;
        }

        this.realTimeByCityData = data;
        // Atualizar realTimeData para compatibilidade com template
        this.realTimeData = data as any;
        this.realTimeError = '';

        // Buscar média diária usando o stationId retornado
        if (data.stationId) {
          this.fetchDailyAverage(data.stationId);
        }
      },
      error: (error) => {
        // Limpar dados anteriores
        this.realTimeByCityData = null;
        this.realTimeData = null;
        this.dailyAverageData = null;

        // Definir mensagem de erro específica
        if (error.status === 0) {
          this.realTimeError = this.ERR_SERVER_UNAVAILABLE;
        } else {
          // Mensagem amigável para qualquer erro (404, 500, etc.)
          this.realTimeError = this.ERR_NO_REALTIME_CITY;
        }
      },
    });
  }
}
